import React, { useState, useContext, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router';
import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { PageHeader, Section, SpinnerArea } from '../../../../shared/components';
import GrievanceForm from './GrievanceForm';
import { GrievanceForm as GrievanceFormType } from '../../../../services/types/Grievance';
import grievanceService from '../../../../services/grievanceService';
import MainContext from '../../../App/MainProvider/context';

const UNKNOWN_ERROR = 'Unknown error occurred';
const GRIEVANCE_PROTOCOL_PATH = '/grievance-protocol';

interface RouteParams {
  number?: string;
}

const GrievanceFormContainer: React.FC = () => {
  const navigate = useNavigate();
  const { number } = useParams() as unknown as RouteParams;
  // const { id } = useParams<RouteParams>();
  const { notify, notifyError } = useContext(MainContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setRelatedOperationIds] = useState<number[]>([]);
  const [, setPrimaryGrievanceId] = useState<number[]>([]);
  const [, setPreviousGrievanceId] = useState<number[]>([]);
  const [formState, setFormState] = useState<GrievanceFormType | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const isEditMode = !!number;
  const { t } = useTranslation();

  useEffect(() => {
    const loadGrievance = async () => {
      if (isEditMode && number) {
        setLoading(true);
        try {
          // const grievanceData = await grievanceService.getGrievanceById(parseInt(id));
          const grievanceData = await grievanceService.getGrievanceByNumber(number);
          // Convert string dates to Date objects
          const formData: GrievanceFormType = {
            ...grievanceData,
          };
          setFormState(formData);
        } catch (error) {
          notifyError(error instanceof Error ? error : new Error(UNKNOWN_ERROR));
          navigate(GRIEVANCE_PROTOCOL_PATH);
        } finally {
          setLoading(false);
        }
      }
    };

    loadGrievance();
  }, [number, isEditMode, navigate, notifyError]);

  const handleSubmit = async (values: GrievanceFormType, resetFormCallback: () => void) => {
    setIsSubmitting(true);

    try {
      const payload = {
        ...values,
      };

      const saveResult = await grievanceService.saveGrievance(payload);

      let resultMessage = isEditMode
        ? `Grievance ${payload.number} updated successfully`
        : `Grievance ${saveResult.grievanceNumber} created successfully`;

      if (isEditMode && saveResult.grievanceNumber !== payload.number) {
        resultMessage += `\n\rNew Grievance Number: ${saveResult.grievanceNumber}`;
      }
      let color = 'success';
      if (saveResult.documentsWarning) {
        color = 'warning';
        resultMessage += `\n\r${saveResult.documentsWarning}`;
      }

      notify('Success', resultMessage, color, 'check-circle');
      // flushSync fuerza a que formik.dirty (via resetForm) quede en false
      // ANTES del navigate() de abajo - sin esto, RouteLeavingGuardGrv (que
      // arma su predicado de bloqueo con formik.dirty) sigue viendo el valor
      // "true" del render anterior en el mismo tick y muestra el popup de
      // "Discard Changes" pese a que el guardado ya terminó bien. Mismo
      // patrón/fix ya usado en SectorTabContainerBase.tsx (Calculator, F4-4).
      flushSync(() => {
        resetFormCallback();
      });
      if (isEditMode && saveResult.grievanceNumber) {
        navigate(`/grievance-protocol/details/${saveResult.grievanceNumber}`);
      } else {
        navigate(GRIEVANCE_PROTOCOL_PATH);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving grievance:', error);
      notifyError(error instanceof Error ? error : new Error(UNKNOWN_ERROR));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number, grievanceNumber: string) => {
    setIsSubmitting(true);

    try {
      await grievanceService.deleteGrievance(id);
      notify(t('success'), `Grievance ${grievanceNumber} deleted successfully`, 'success');
      navigate(GRIEVANCE_PROTOCOL_PATH);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting grievance:', error);
      notifyError(error instanceof Error ? error : new Error(UNKNOWN_ERROR));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(
      isEditMode && formState
        ? `/grievance-protocol/details/${formState.number}`
        : GRIEVANCE_PROTOCOL_PATH
    );
  };

  const getPageTitle = () => {
    if (loading) {
      return t('');
    }
    return isEditMode && formState ? formState.number : 'Create new Grievance';
  };

  return (
    <Container>
      <PageHeader
        title={getPageTitle()}
        backUrl={
          isEditMode && formState
            ? `/grievance-protocol/details/${formState.number}`
            : GRIEVANCE_PROTOCOL_PATH
        }
      />
      <Row>
        <Section>
          <SpinnerArea loading={loading}>
            <div className="content-grievance-form">
              <GrievanceForm
                handleSubmit={handleSubmit}
                handleCancel={handleCancel}
                handleDelete={handleDelete}
                setRelatedOperationId={setRelatedOperationIds}
                setPrimaryGrievanceId={setPrimaryGrievanceId}
                setPreviousGrievanceId={setPreviousGrievanceId}
                isSubmitting={isSubmitting}
                formState={formState}
                isEditMode={isEditMode}
              />
            </div>
          </SpinnerArea>
        </Section>
      </Row>
    </Container>
  );
};

export default GrievanceFormContainer;
